import { useState } from 'react';
import { ApiError } from '../../../api';
import { Button, Drawer, Field, Select, TextArea, TextInput } from '../../../shell/kit';
import { useToast } from '../../../shell/toast';
import type { IdentityKey, IdentityTranslate } from '../i18n';
import {
  VEHICLE_BODY_TYPES,
  requestPhoneCode,
  updateProfile,
  updateVehicle,
  verifyPhoneCode,
  type CardVehicleRow,
} from './api';

/**
 * Правка карточки клиента: профиль, атрибуты автомобиля, подтверждение
 * телефона кодом (ТЗ §6.1, М1.4, §5.3.2). До этого карточка только читала —
 * имя, e-mail, марку и кузов нельзя было исправить нигде, кроме базы.
 */

function useFail(): (err: unknown) => void {
  const toast = useToast();
  return (err: unknown) => {
    if (err instanceof ApiError) toast.error(err.message, err.code);
    else toast.error(String((err as Error).message));
  };
}

export function ProfileEditor({
  t,
  customerId,
  displayName,
  email,
  canEditEmail,
  onClose,
  onSaved,
}: {
  t: IdentityTranslate;
  customerId: string;
  displayName: string | null;
  email: string | null;
  /** E-mail правит только тот, кому видны ПДн (`customer.view_pii`). */
  canEditEmail: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const fail = useFail();
  const [name, setName] = useState(displayName ?? '');
  const [mail, setMail] = useState(email ?? '');
  const [busy, setBusy] = useState(false);

  const save = async (): Promise<void> => {
    setBusy(true);
    try {
      await updateProfile(customerId, {
        displayName: name.trim() || null,
        ...(canEditEmail ? { email: mail.trim() || null } : {}),
      });
      toast.ok(t('identity.card.saved'));
      onSaved();
      onClose();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer
      title={t('identity.card.editProfile')}
      onClose={onClose}
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>{t('identity.card.cancel')}</Button>
          <Button tone="primary" disabled={busy} onClick={() => void save()}>
            {t('identity.card.save')}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label={t('identity.card.name')}>
          <TextInput value={name} maxLength={200} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label={t('identity.card.email')} hint={t('identity.card.emailHint')}>
          <TextInput
            type="email"
            disabled={!canEditEmail}
            value={mail}
            maxLength={200}
            onChange={(e) => setMail(e.target.value)}
          />
        </Field>
      </div>
    </Drawer>
  );
}

export function VehicleEditor({
  t,
  vehicle,
  onClose,
  onSaved,
}: {
  t: IdentityTranslate;
  vehicle: CardVehicleRow;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const fail = useFail();
  const [make, setMake] = useState(vehicle.make ?? '');
  const [model, setModel] = useState(vehicle.model ?? '');
  const [bodyType, setBodyType] = useState(vehicle.body_type ?? '');
  const [sizeClass, setSizeClass] = useState(vehicle.size_class ?? '');
  const [coating, setCoating] = useState(vehicle.coating_notes ?? '');
  const [busy, setBusy] = useState(false);

  const save = async (): Promise<void> => {
    setBusy(true);
    try {
      await updateVehicle(vehicle.id, {
        make: make.trim() || null,
        model: model.trim() || null,
        bodyType: bodyType || null,
        sizeClass: sizeClass || null,
        coatingNotes: coating.trim() || null,
      });
      toast.ok(t('identity.card.saved'));
      onSaved();
      onClose();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer
      title={t('identity.card.editVehicle', { plate: vehicle.plate })}
      onClose={onClose}
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>{t('identity.card.cancel')}</Button>
          <Button tone="primary" disabled={busy} onClick={() => void save()}>
            {t('identity.card.save')}
          </Button>
        </div>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={t('identity.card.make')}>
          <TextInput value={make} maxLength={80} onChange={(e) => setMake(e.target.value)} />
        </Field>
        <Field label={t('identity.card.model')}>
          <TextInput value={model} maxLength={80} onChange={(e) => setModel(e.target.value)} />
        </Field>
        <Field label={t('identity.card.bodyType')}>
          <Select value={bodyType} onChange={(e) => setBodyType(e.target.value)}>
            <option value="">—</option>
            {VEHICLE_BODY_TYPES.map((code) => (
              <option key={code} value={code}>
                {t(`identity.body.${code}` as IdentityKey)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('identity.card.sizeClass')}>
          <Select value={sizeClass} onChange={(e) => setSizeClass(e.target.value)}>
            <option value="">—</option>
            {(['s', 'm', 'l', 'xl'] as const).map((code) => (
              <option key={code} value={code}>
                {code.toUpperCase()}
              </option>
            ))}
          </Select>
        </Field>
        <div className="sm:col-span-2">
          <Field label={t('identity.card.coating')} hint={t('identity.card.coatingHint')}>
            <TextArea
              rows={3}
              value={coating}
              maxLength={1000}
              onChange={(e) => setCoating(e.target.value)}
            />
          </Field>
        </div>
      </div>
    </Drawer>
  );
}

/**
 * Подтверждение телефона кодом: код уходит клиенту SMS, оператор вводит его со
 * слов клиента. Успешная проверка проставляет `phone_verified_at` в профиле.
 */
export function PhoneVerifier({
  t,
  phone,
  onVerified,
}: {
  t: IdentityTranslate;
  phone: string;
  onVerified: () => void;
}) {
  const toast = useToast();
  const fail = useFail();
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);

  const send = async (): Promise<void> => {
    setBusy(true);
    try {
      await requestPhoneCode(phone);
      setSent(true);
      toast.ok(t('identity.card.codeSent'));
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const check = async (): Promise<void> => {
    setBusy(true);
    try {
      await verifyPhoneCode(phone, code.trim());
      toast.ok(t('identity.card.phoneVerifiedNow'));
      setSent(false);
      setCode('');
      onVerified();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  if (!sent) {
    return (
      <Button size="sm" disabled={busy} onClick={() => void send()}>
        {t('identity.card.sendCode')}
      </Button>
    );
  }
  return (
    <div className="flex items-center gap-2">
      <TextInput
        inputMode="numeric"
        maxLength={6}
        className="w-24"
        aria-label={t('identity.card.code')}
        placeholder={t('identity.card.code')}
        value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
      />
      <Button size="sm" tone="primary" disabled={busy || code.length !== 6} onClick={() => void check()}>
        {t('identity.card.checkCode')}
      </Button>
    </div>
  );
}
